import { useCallback, useState, useEffect } from "react";
import { AccountFormData, SessionInfo } from "@features/settings/model/types";
import { userService } from "@features/settings/service/userService";
import { useToast } from "@/core/hooks/useToast";
import { useUnit } from "effector-react";
import { $userSettings, fetchUserSettings } from "../store";

export const AccountViewModel = () => {
  const [loading, setLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const { showError, showSuccess } = useToast();
  const [formData, setFormData] = useState<AccountFormData>({
    emailAddress: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
    twoFactorEnabled: false,
  });

  const userSettings = useUnit($userSettings);

  // Fetch user settings on mount
  useEffect(() => {
    fetchUserSettings();
  }, []);

  // Update form data when user settings are loaded
  useEffect(() => {
    if (userSettings?.email) {
      setFormData((prev) => ({
        ...prev,
        emailAddress: userSettings.email,
      }));
    }
  }, [userSettings]);

  const [sessions, setSessions] = useState<SessionInfo[]>([
    {
      id: "current",
      device: "Chrome on macOS",
      location: "Athens, GR",
      time: "Active now",
    },
    {
      id: "safari",
      device: "Safari on iPhone",
      location: "Athens, GR",
      time: "2 days ago",
    },
  ]);

  const handleInputChange = useCallback(
    (field: keyof AccountFormData, value: any) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const signOutSession = useCallback((sessionId: string) => {
    setSessions((prev) => prev.filter((s) => s.id !== sessionId));
  }, []);

  const handleSaveNewEmail = useCallback(async () => {
    setLoading(true);
    try {
      await userService.updateUserSettings({
        old_password: formData.currentPassword,
        new_email: formData.emailAddress,
      });
      showSuccess("Email updated successfully");
      // Refresh user settings after update
      fetchUserSettings();
    } catch (error) {
      showError("Failed to update email");
    } finally {
      setLoading(false);
    }
  }, [formData, showError, showSuccess]);

  const handleSaveNewPass = useCallback(async () => {
    if (formData.newPassword !== formData.confirmPassword) {
      showError("New passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await userService.updateUserSettings({
        old_password: formData.currentPassword,
        new_password: formData.newPassword,
      });
      showSuccess("Password updated successfully");
    } catch (error) {
      showError("Failed to update password");
    } finally {
      setLoading(false);
    }
  }, [formData, showError, showSuccess]);

  const handleDeleteAccount = useCallback(async () => {
    setDeleteLoading(true);
    try {
      await userService.deleteUserAccount();
      showSuccess("Account deleted successfully");
      // Clear local storage and redirect to login
      localStorage.clear();
      window.location.href = "/login";
    } catch (error) {
      showError("Failed to delete account");
    } finally {
      setDeleteLoading(false);
      setDeleteModalOpen(false);
    }
  }, [showError, showSuccess]);

  const handleCancel = useCallback(() => {
    // console.log("Cancelled");
  }, []);

  return {
    loading,
    formData,
    sessions,
    deleteModalOpen,
    deleteLoading,
    setDeleteModalOpen,
    handleInputChange,
    signOutSession,
    handleSaveNewEmail,
    handleSaveNewPass,
    handleDeleteAccount,
    handleCancel,
  };
};
